import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { generateEventSlug, getLocalDateKey, parseEventDate } from '../utils/eventUtils';
import makeMarkdownComponents from '../utils/markdownComponents';
import { themes } from '../config/themes';
import { CATEGORIES, catColor, categorizeEvent } from '../config/categories';
import CalendarLegend from './calendar/CalendarLegend';
import { fs } from '../utils/typography';

const DOW_SHORT = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const ordinalSuffix = (day) => {
    if (day >= 11 && day <= 13) return 'th';
    switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
    }
};

const formatDigestDate = (date) => {
    const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const day = date.getDate();
    return `${weekday}, ${month} ${day}${ordinalSuffix(day)}`;
};

const formatTimeRange = (event) => {
    const start = parseEventDate(event.startDate);
    if (!start || (start.getHours() === 0 && start.getMinutes() === 0)) {
        return null; // all-day
    }
    const end = parseEventDate(event.endDate || event.startDate);
    const opts = { hour: 'numeric', minute: '2-digit' };
    const startStr = start.toLocaleTimeString([], opts);
    return end && end.getTime() !== start.getTime()
        ? `${startStr} – ${end.toLocaleTimeString([], opts)}`
        : startStr;
};

// List view — "Atlanta Editorial" design refresh: an agenda of day groups,
// each with a date rail (mono weekday + serif day number) beside event rows
// carrying a category accent bar, mono time column, category chip, and a
// chevron register affordance. Rows expand in place for full details.
const EventListView = (props) => {
    const theme = props.theme || themes.light;
    const tokens = theme?.tokens || themes.light.tokens;
    const c = tokens.colors;

    const [expandedEventId, setExpandedEventId] = useState(null);
    const [groupedEvents, setGroupedEvents] = useState({});
    const [isCurrentMonth, setIsCurrentMonth] = useState(false);
    const [showPastEvents, setShowPastEvents] = useState(false);
    const [digestMode, setDigestMode] = useState(() => window.location.hash === '#email');

    // Sync digest mode with URL hash
    useEffect(() => {
        const onHash = () => setDigestMode(window.location.hash === '#email');
        window.addEventListener('hashchange', onHash);
        return () => window.removeEventListener('hashchange', onHash);
    }, []);

    const toggleDigestMode = () => {
        if (digestMode) {
            window.history.pushState('', document.title, window.location.pathname + window.location.search);
            setDigestMode(false);
        } else {
            window.location.hash = 'email';
            setDigestMode(true);
        }
    };

    const copyDigestAsText = () => {
        const lines = [];
        const siteOrigin = window.location.origin;
        Object.entries(groupedEvents).forEach(([dateKey, dateEvents]) => {
            const date = parseEventDate(dateKey);
            if (date) {
                lines.push(`\n${formatDigestDate(date)}`);
                lines.push('─'.repeat(48));
            }
            dateEvents.forEach(ev => {
                const time = formatTimeRange(ev);
                lines.push(`${ev.title}`);
                if (ev.organization) lines.push(`  ${ev.organization}`);
                if (time) lines.push(`  ${time}`);
                if (ev.location) lines.push(`  ${ev.location}`);
                lines.push(`  View Details: ${siteOrigin}/#${generateEventSlug(ev)}`);
                lines.push('');
            });
        });
        navigator.clipboard.writeText(lines.join('\n').trim());
        if (window.showToast) window.showToast('Digest copied to clipboard!');
    };

    useEffect(() => {
        setIsCurrentMonth(false);

        if (!props.events || props.events.length === 0) {
            setGroupedEvents({});
            return;
        }

        let filtered = props.events;

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const onCurrentMonth = (today.getMonth() + 1 === props.curMonth) && (today.getFullYear() === props.curYear);
        setIsCurrentMonth(onCurrentMonth);

        if (onCurrentMonth && !showPastEvents) {
            filtered = filtered.filter(event => {
                const eventDate = parseEventDate(event.endDate || event.startDate);
                return eventDate && eventDate >= today;
            });
        }

        const grouped = {};
        filtered.forEach(event => {
            const key = getLocalDateKey(event.startDate);
            if (!key) return;
            (grouped[key] = grouped[key] || []).push(event);
        });

        const sortedGrouped = {};
        Object.keys(grouped).sort().forEach(key => {
            sortedGrouped[key] = grouped[key].sort(
                (a, b) => (parseEventDate(a.startDate) || 0) - (parseEventDate(b.startDate) || 0)
            );
        });

        setGroupedEvents(sortedGrouped);
    }, [props.events, props.curMonth, props.curYear, showPastEvents]);

    const toggleEventDetails = (eventId) => {
        setExpandedEventId(expandedEventId === eventId ? null : eventId);
    };

    const renderEmptyState = (icon, heading, body) => (
        <div className="rounded-lg p-8 text-center" style={{ background: c.surfaceAlt, border: `1px solid ${c.gridLine}` }} role="status" aria-live="polite">
            <div className="flex flex-col items-center">
                <i className={`fas ${icon} text-4xl mb-4`} style={{ color: c.accent }} aria-hidden="true"></i>
                <h3 className="text-xl mb-2" style={{ fontFamily: tokens.fonts.display, fontWeight: 600, color: c.ink }}>{heading}</h3>
                <p style={{ color: c.muted }}>{body}</p>
            </div>
        </div>
    );

    const renderEventRow = (event, isLast) => {
        const cat = categorizeEvent(event);
        const cc = catColor(cat, tokens);
        const time = formatTimeRange(event);
        const isExpanded = expandedEventId === event.id;
        const hasEventPage = event.eventURL && (() => {
            const eventEnd = parseEventDate(event.endDate || event.startDate);
            return eventEnd && eventEnd >= new Date(new Date().setHours(0, 0, 0, 0));
        })();

        return (
            <div key={event.id} style={{ borderBottom: (isLast && !isExpanded) ? 'none' : `1px solid ${c.gridLine}` }} role="listitem">
                <div
                    className="flex items-center cursor-pointer gap-3 md:gap-4"
                    style={{ padding: '14px 4px 14px 18px', position: 'relative' }}
                    onClick={() => toggleEventDetails(event.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleEventDetails(event.id);
                        }
                    }}
                    aria-expanded={isExpanded}
                    aria-label={`${isExpanded ? 'Collapse' : 'Expand'} details for ${event.title}`}
                >
                    {/* category accent bar */}
                    <div style={{ position: 'absolute', left: 0, top: 10, bottom: 10, width: 3, borderRadius: 2, background: cc.strong }} />
                    {/* time */}
                    <div className="hidden sm:block flex-none" style={{
                        width: 132, fontFamily: tokens.fonts.mono, fontSize: fs(11), fontWeight: 500,
                        color: time ? c.muted : c.faint, letterSpacing: 0.2,
                    }}>
                        {time || 'All day'}
                    </div>
                    {/* title + org */}
                    <div className="flex-1 min-w-0">
                        <div style={{ fontFamily: tokens.fonts.ui, fontSize: fs(15.5), fontWeight: 600, color: c.ink, lineHeight: 1.3, letterSpacing: -0.1 }}>
                            {event.eventType === 'Featured' && (
                                <i className="fas fa-star" style={{ color: cc.strong, fontSize: fs(11), marginRight: 6 }} aria-label="Featured event" />
                            )}
                            {event.title}
                        </div>
                        <div style={{ fontFamily: tokens.fonts.ui, fontSize: fs(12.5), color: c.muted, marginTop: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                            <span>
                                {event.organization}
                                <span className="sm:hidden" style={{ fontFamily: tokens.fonts.mono, fontSize: fs(10.5), color: c.faint }}>
                                    {' '}· {time || 'All day'}
                                </span>
                            </span>
                            {event.sponsorLogo && (
                                <img src={event.sponsorLogo} alt={event.sponsorName || 'Sponsor'} style={{ maxHeight: 14, maxWidth: 80, objectFit: 'contain', opacity: 0.85, verticalAlign: 'middle' }} />
                            )}
                        </div>
                    </div>
                    {/* category chip */}
                    <span className="hidden lg:inline-flex flex-none" style={{
                        fontFamily: tokens.fonts.mono, fontSize: fs(9.5), letterSpacing: 0.5, textTransform: 'uppercase', fontWeight: 600,
                        color: cc.fillText, background: cc.fillBg, border: `1px solid ${cc.line}`, padding: '4px 9px', borderRadius: 6,
                    }}>{CATEGORIES[cat].label}</span>
                    {/* register affordance */}
                    {hasEventPage ? (
                        <a
                            href={event.eventURL}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex-none flex items-center justify-center"
                            style={{ width: 30, height: 30, borderRadius: 8, border: `1px solid ${c.gridLine}`, color: c.accent }}
                            aria-label={`Go to event page for ${event.title}`}
                            title="Go to event page"
                        >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 3l4 4-4 4" /></svg>
                        </a>
                    ) : (
                        <span className="flex-none flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: 8, border: `1px solid ${c.gridLine}`, color: c.faint }}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={isExpanded ? 'M3 9l4-4 4 4' : 'M3 5l4 4 4-4'} /></svg>
                        </span>
                    )}
                </div>

                {isExpanded && (
                    <div
                        style={{ margin: '0 4px 14px 18px', padding: '14px 16px', borderRadius: 9, background: c.surfaceAlt, border: `1px solid ${c.gridLine}` }}
                        role="region"
                        aria-label={`Details for ${event.title}`}
                    >
                        {event.description && (
                            <div className="mb-3" style={{ fontFamily: tokens.fonts.ui, fontSize: fs(12.5), lineHeight: 1.55, color: c.muted }}>
                                <ReactMarkdown components={makeMarkdownComponents(theme)}>{event.description}</ReactMarkdown>
                            </div>
                        )}
                        {event.location && (
                            <div className="flex gap-2 items-start mb-3">
                                <span style={{
                                    fontFamily: tokens.fonts.mono, fontSize: fs(9.5), letterSpacing: 0.5, textTransform: 'uppercase',
                                    color: c.faint, fontWeight: 600, width: 64, flex: '0 0 auto', paddingTop: 1,
                                }}>Location</span>
                                <span style={{ fontFamily: tokens.fonts.ui, fontSize: fs(12.5), color: c.ink, fontWeight: 500, lineHeight: 1.4 }}>{event.location}</span>
                            </div>
                        )}
                        {Array.isArray(event.tags) && event.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                                {event.tags.map((tag, index) => (
                                    <span key={index} style={{
                                        fontFamily: tokens.fonts.mono, fontSize: fs(9.5), letterSpacing: 0.5, textTransform: 'uppercase',
                                        fontWeight: 600, color: c.muted, border: `1px solid ${c.gridLine}`, padding: '3px 7px', borderRadius: 5,
                                    }}>
                                        {typeof tag === 'string' ? tag : tag?.name || ''}
                                    </span>
                                ))}
                            </div>
                        )}
                        {event.sponsorLogo && (
                            <div className="flex items-center gap-3 mb-3">
                                <span style={{ fontFamily: tokens.fonts.mono, fontSize: fs(9.5), letterSpacing: 0.5, textTransform: 'uppercase', color: c.faint, fontWeight: 600, flex: '0 0 auto' }}>Hosted by</span>
                                <img src={event.sponsorLogo} alt={event.sponsorName || 'Sponsor'} style={{ maxHeight: 22, maxWidth: 120, objectFit: 'contain', opacity: 0.85 }} />
                            </div>
                        )}
                        <div className="flex items-center gap-3 flex-wrap">
                            {hasEventPage && (
                                <a
                                    href={event.eventURL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        padding: '8px 16px', borderRadius: 9, background: c.accent, color: c.todayInk,
                                        fontFamily: tokens.fonts.ui, fontSize: fs(13), fontWeight: 700,
                                    }}
                                    aria-label={`Go to event page for ${event.title}`}
                                >
                                    {event.eventURL === '/private-events' ? 'About private events' : 'Register'}
                                </a>
                            )}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const slug = generateEventSlug(event);
                                    navigator.clipboard.writeText(`${window.location.origin}/#${slug}`);
                                    if (window.showToast) window.showToast('Event URL copied to clipboard!');
                                }}
                                style={{
                                    padding: '8px 14px', borderRadius: 9, border: `1px solid ${c.gridLine}`, color: c.muted,
                                    fontFamily: tokens.fonts.ui, fontSize: fs(13), fontWeight: 600, background: 'transparent',
                                }}
                            >
                                <i className="fas fa-share-alt mr-2" aria-hidden="true"></i>Share
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderDigestEventRow = (event, isLast) => {
        const cat = categorizeEvent(event);
        const cc = catColor(cat, tokens);
        const time = formatTimeRange(event);
        const slug = generateEventSlug(event);
        const siteUrl = `${window.location.origin}/#${slug}`;
        return (
            <div key={event.id} style={{ borderBottom: isLast ? 'none' : `1px solid ${c.gridLine}`, padding: '11px 4px 11px 18px', position: 'relative' }}>
                <div style={{ position: 'absolute', left: 0, top: 8, bottom: 8, width: 3, borderRadius: 2, background: cc.strong }} />
                <div style={{ fontFamily: tokens.fonts.ui, fontSize: fs(15), fontWeight: 600, color: c.ink, lineHeight: 1.3, marginBottom: 2 }}>
                    {event.eventType === 'Featured' && <i className="fas fa-star" style={{ color: cc.strong, fontSize: fs(10), marginRight: 5 }} aria-hidden="true" />}
                    {event.title}
                </div>
                <div style={{ fontFamily: tokens.fonts.ui, fontSize: fs(12.5), color: c.muted, display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
                    {event.organization && <span>{event.organization}</span>}
                    {time && <span style={{ fontFamily: tokens.fonts.mono, fontSize: fs(11), color: c.faint }}>{time}</span>}
                    {event.location && <span style={{ color: c.faint }}>{event.location}</span>}
                    {event.sponsorLogo && (
                        <img src={event.sponsorLogo} alt={event.sponsorName || 'Sponsor'} style={{ maxHeight: 14, maxWidth: 80, objectFit: 'contain', opacity: 0.85, marginTop: 2 }} />
                    )}
                </div>
                <a
                    href={siteUrl}
                    style={{ fontFamily: tokens.fonts.mono, fontSize: fs(10.5), color: c.accent, letterSpacing: 0.2, marginTop: 4, display: 'inline-block' }}
                    className="hover:underline"
                >
                    View Details
                </a>
            </div>
        );
    };

    const renderDayGroup = (dateKey, dateEvents) => {
        const date = parseEventDate(dateKey);
        const monthName = date ? date.toLocaleString('default', { month: 'short' }) : '';
        return (
            <div key={dateKey} className="flex gap-4 md:gap-6" style={{ padding: '22px 0', borderTop: `1px solid ${c.gridLine}` }} role="listitem">
                {/* date rail */}
                <div className="flex-none text-right w-14 md:w-24">
                    <div style={{ fontFamily: tokens.fonts.mono, fontSize: fs(11), letterSpacing: 0.8, textTransform: 'uppercase', fontWeight: 600, color: c.muted }}>
                        {date ? DOW_SHORT[date.getDay()] : ''}
                    </div>
                    <div style={{ fontFamily: tokens.fonts.display, fontSize: fs(46), fontWeight: 500, lineHeight: 1, color: c.ink, marginTop: 3 }}>
                        {date ? date.getDate() : ''}
                    </div>
                    <div style={{ fontFamily: tokens.fonts.ui, fontSize: fs(11.5), color: c.faint, marginTop: 4 }}>{monthName}</div>
                </div>
                {/* event rows */}
                <div className="flex-1 min-w-0" role="list">
                    {dateEvents.map((event, j) => renderEventRow(event, j === dateEvents.length - 1))}
                </div>
            </div>
        );
    };

    return (
        <div
            className="w-full h-full flex flex-col"
            style={{ background: c.surface, color: c.ink, fontFamily: tokens.fonts.ui, textAlign: 'left' }}
            role="region"
            aria-label="Event List"
        >
            <div className="flex-1 min-h-0 overflow-y-auto">
                <div style={{ maxWidth: 1180, margin: '0 auto', padding: '0 16px 30px' }}>
                    <div className="flex justify-between items-center pt-3 flex-wrap gap-2">
                        {isCurrentMonth ? (
                            <button
                                onClick={() => setShowPastEvents(prev => !prev)}
                                style={{
                                    fontFamily: tokens.fonts.mono, fontSize: fs(10.5), letterSpacing: 0.5, textTransform: 'uppercase',
                                    fontWeight: 600, color: showPastEvents ? c.accent : c.muted,
                                    border: `1px solid ${showPastEvents ? c.accent : c.gridLine}`,
                                    borderRadius: 8, padding: '6px 12px', background: 'transparent',
                                }}
                                aria-pressed={showPastEvents}
                                aria-label={showPastEvents ? 'Hide past events' : 'Show past events'}
                            >
                                <i className={`fas fa-${showPastEvents ? 'eye-slash' : 'eye'} mr-2`} aria-hidden="true"></i>
                                {showPastEvents ? 'Hide' : 'Show'} past events
                            </button>
                        ) : <span />}
                        {digestMode && (
                            <button
                                onClick={toggleDigestMode}
                                style={{
                                    fontFamily: tokens.fonts.mono, fontSize: fs(10.5), letterSpacing: 0.5, textTransform: 'uppercase',
                                    fontWeight: 600, color: c.accent,
                                    border: `1px solid ${c.accent}`,
                                    borderRadius: 8, padding: '6px 12px', background: 'transparent',
                                }}
                                aria-pressed={digestMode}
                                aria-label="Exit email digest mode"
                                title="Shareable list with direct links to each event"
                            >
                                <i className="fas fa-envelope mr-2" aria-hidden="true"></i>
                                Email digest
                            </button>
                        )}
                    </div>

                    {digestMode && Object.keys(groupedEvents).length > 0 && (
                        <div style={{
                            margin: '12px 0 0', padding: '12px 16px', borderRadius: 9,
                            background: `color-mix(in srgb, ${c.accent} 10%, ${c.surface})`,
                            border: `1px solid ${c.accent}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
                        }}>
                            <span style={{ fontFamily: tokens.fonts.ui, fontSize: fs(13), color: c.ink }}>
                                <i className="fas fa-envelope mr-2" style={{ color: c.accent }} aria-hidden="true" />
                                Email digest — each event includes a direct link back to this site.
                            </span>
                            <button
                                onClick={copyDigestAsText}
                                style={{
                                    fontFamily: tokens.fonts.mono, fontSize: fs(10.5), letterSpacing: 0.4, textTransform: 'uppercase',
                                    fontWeight: 600, background: c.accent, color: tokens.mode === 'dark' ? '#1a1410' : '#fff',
                                    border: 'none', borderRadius: 7, padding: '6px 14px', cursor: 'pointer',
                                }}
                                aria-label="Copy digest as plain text"
                            >
                                <i className="fas fa-copy mr-1.5" aria-hidden="true" />Copy as text
                            </button>
                        </div>
                    )}

                    {(!props.events || props.events.length === 0) ? (
                        <div className="pt-6">
                            {renderEmptyState('fa-calendar-times', 'No Events Found', 'There are no events added for this month.')}
                        </div>
                    ) : Object.keys(groupedEvents).length === 0 ? (
                        <div className="pt-6">
                            {renderEmptyState('fa-filter', 'No Matching Events', 'No events match your selected filter.')}
                        </div>
                    ) : digestMode ? (
                        <div role="list" className="mt-3">
                            {Object.entries(groupedEvents).map(([dateKey, dateEvents]) => {
                                const date = parseEventDate(dateKey);
                                return (
                                    <div key={dateKey} className="flex gap-4 md:gap-6" style={{ padding: '18px 0', borderTop: `1px solid ${c.gridLine}` }} role="listitem">
                                        <div className="flex-none text-right w-32 md:w-48">
                                            <h3 style={{ fontFamily: tokens.fonts.display, fontSize: fs(22), fontWeight: 500, lineHeight: 1.15, color: c.ink, margin: 0 }}>
                                                {date ? formatDigestDate(date) : ''}
                                            </h3>
                                        </div>
                                        <div className="flex-1 min-w-0" role="list">
                                            {dateEvents.map((ev, j) => renderDigestEventRow(ev, j === dateEvents.length - 1))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div role="list" className="mt-3">
                            {Object.entries(groupedEvents).map(([dateKey, dateEvents]) => renderDayGroup(dateKey, dateEvents))}
                        </div>
                    )}
                </div>
            </div>
            <CalendarLegend tokens={tokens} />
        </div>
    );
};

export default EventListView;
